import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Gift, Trophy, Sparkles, CheckCircle2, ArrowRight, Scale, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoneInput } from "@/components/PhoneInput";
import { useBranding } from "@/context/BrandingContext";

export default function ReferralLanding() {
    const { code } = useParams();
    const navigate = useNavigate();
    const { register } = useAuth();
    const branding = useBranding();
    const logoH = Math.max(20, Math.min(40, Number(branding.logo_display_height) || 28));
    const showIcon = (branding.logo_layout || "icon_and_text") !== "text_only";
    const showText = (branding.logo_layout || "icon_and_text") !== "icon_only";
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", firm_name: "", bar_council_enrollment_no: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancel = false;
        api.get(`/referrals/public/${code}`)
            .then(r => { if (!cancel) { setInfo(r.data); setLoading(false); } })
            .catch(e => {
                if (cancel) return;
                setError(apiError(e) || "Invalid referral link");
                setLoading(false);
            });
        return () => { cancel = true; };
    }, [code]);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await register({ ...form, referral_code: code });
            toast.success("Welcome to Law OS!");
            navigate("/app");
        } catch (err) { toast.error(apiError(err)); }
        finally { setSubmitting(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground" data-testid="refer-loading">Loading invite…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-6">
                <div className="text-center max-w-md" data-testid="refer-invalid">
                    <Scale className="w-12 h-12 text-primary mx-auto mb-6" strokeWidth={1.2}/>
                    <h1 className="text-2xl font-serif text-foreground">Invitation not found</h1>
                    <p className="text-sm text-muted-foreground mt-3">
                        This referral link is invalid or has expired. You can still sign up to Law OS — head over to the main registration page.
                    </p>
                    <Link to="/register" className="inline-block mt-6 px-6 py-3 border border-primary text-primary text-xs uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="refer-fallback-register">
                        Continue to sign up
                    </Link>
                </div>
            </div>
        );
    }

    const refLine = info.firm_name
        ? `${info.honorific ? info.honorific + " " : ""}${info.referrer_first_name} from ${info.firm_name}`
        : `${info.honorific ? info.honorific + " " : ""}${info.referrer_first_name}`;

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="refer-landing">
            {/* Hero */}
            <div className="border-b border-card relative overflow-hidden">
                {/* Decorative grain */}
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
                    backgroundImage: "radial-gradient(circle at 20% 20%, #D4AF37 0%, transparent 50%), radial-gradient(circle at 80% 80%, #D4AF37 0%, transparent 50%)"
                }}/>
                <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 relative">
                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        {/* Left — pitch */}
                        <div className="lg:col-span-7 space-y-8">
                            <div className="flex items-center gap-2">
                                {showIcon && (branding.logo_src ? (
                                    <img src={branding.logo_src} alt={branding.app_name}
                                        style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                                        className="object-contain"
                                        onError={(e) => { e.target.style.display = "none"; }}/>
                                ) : (
                                    <Scale className="w-5 h-5 text-primary" strokeWidth={1.2}/>
                                ))}
                                {showText && <span className="text-xs uppercase tracking-[0.3em] text-primary">{branding.app_name || "Law OS"}</span>}
                            </div>

                            <div className="space-y-3">
                                <div className="inline-flex items-center gap-2 px-3 py-1 border border-primary/40 bg-primary/5 text-[10px] uppercase tracking-[0.2em] text-primary" data-testid="refer-invitation-badge">
                                    <Gift className="w-3 h-3" strokeWidth={1.5}/>
                                    You've been personally invited
                                </div>
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.05]">
                                    <span className="block text-foreground">{refLine}</span>
                                    <span className="block text-primary mt-2">has invited you to Law OS.</span>
                                </h1>
                                <p className="text-base text-muted-foreground leading-relaxed max-w-xl pt-2">
                                    The first end-to-end LegalOS for Indian advocates and law firms. Cases, hearings, AI-drafted documents, e-signatures, billing — all in one cabinet-style workspace your team will actually enjoy using.
                                </p>
                            </div>

                            {/* Perks */}
                            <div className="grid sm:grid-cols-3 gap-4 pt-6">
                                <Perk icon={Sparkles} label="Free trial" value={`${info.perks.trial_days} days`} testId="refer-perk-trial"/>
                                {info.perks.signup_bonus_enabled && info.perks.signup_bonus_inr > 0 ? (
                                    <Perk icon={ShieldCheck} label="On us" value={`₹${info.perks.signup_bonus_inr} AI credit`} testId="refer-perk-bonus"/>
                                ) : (
                                    <Perk icon={ShieldCheck} label="Quality" value="Bank-grade security" testId="refer-perk-bonus"/>
                                )}
                                <Perk icon={Trophy} label="Referrer earns" value={`${info.perks.commission_pct}% × ${info.perks.commission_cycles} cycles`} testId="refer-perk-commission"/>
                            </div>

                            {/* Why Law OS */}
                            <div className="space-y-3 pt-6">
                                <div className="overline text-muted-foreground">Built for the bar</div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {[
                                        ["Court VC links & cause-list automation", "Never miss a hearing again"],
                                        ["AI Document Generation Studio", "Draft replies, notices, MoAs in minutes"],
                                        ["Strict tenant isolation + passkeys", "Bank-grade security out of the box"],
                                        ["DocuSign + DigiLocker e-sign", "Send for signature in one click"],
                                    ].map(([t, s], i) => (
                                        <div key={i} className="flex gap-3 items-start text-sm">
                                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5}/>
                                            <div>
                                                <div className="text-foreground">{t}</div>
                                                <div className="text-xs text-muted-foreground">{s}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right — signup form */}
                        <div className="lg:col-span-5">
                            <div className="bg-muted border border-border p-6 lg:p-8 space-y-5 sticky top-6" data-testid="refer-signup-card">
                                <div>
                                    <div className="overline">Claim your invitation</div>
                                    <h2 className="text-xl font-serif text-foreground mt-1">Create your firm in 60 seconds</h2>
                                </div>
                                <div className="bg-background border border-primary/30 px-3 py-2 flex items-center gap-2 text-[11px]">
                                    <Zap className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/>
                                    <span className="text-muted-foreground">Code applied:</span>
                                    <code className="text-primary font-mono" data-testid="refer-applied-code">{info.code}</code>
                                </div>

                                <form onSubmit={submit} className="space-y-3">
                                    <Field label="Your name">
                                        <Input required value={form.name} onChange={upd("name")} data-testid="refer-name-input" placeholder="Adv. R. Sharma"/>
                                    </Field>
                                    <Field label="Firm name (optional)">
                                        <Input value={form.firm_name} onChange={upd("firm_name")} data-testid="refer-firm-input" placeholder="Sharma & Co."/>
                                    </Field>
                                    <Field label="Work email">
                                        <Input required type="email" value={form.email} onChange={upd("email")} data-testid="refer-email-input" placeholder="advocate@firm.in"/>
                                    </Field>
                                    <Field label="Mobile (optional)">
                                        <PhoneInput value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} testid="refer-phone"/>
                                    </Field>
                                    <Field label="Choose a password">
                                        <Input required type="password" minLength="6" value={form.password} onChange={upd("password")} data-testid="refer-password-input"/>
                                    </Field>
                                    <Field label="Bar Council Enrollment No." hint="Optional — e.g. MH/1234/2018">
                                        <Input value={form.bar_council_enrollment_no} onChange={upd("bar_council_enrollment_no")} data-testid="refer-bar-council-input" placeholder="MH/1234/2018" className="font-mono"/>
                                    </Field>
                                    <button type="submit" disabled={submitting} data-testid="refer-submit-btn"
                                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.2em] transition-colors">
                                        {submitting ? "Creating your firm…" : (<>Activate my account <ArrowRight className="w-3.5 h-3.5"/></>)}
                                    </button>
                                </form>

                                <p className="text-[10px] text-muted-foreground leading-relaxed">
                                    By creating an account you agree to our terms. Your invitation gives {info.referrer_first_name} a thank-you commission once you subscribe — your pricing is unchanged.
                                </p>
                                <p className="text-xs text-muted-foreground text-center pt-2 border-t border-card">
                                    Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="refer-login-link">Sign in</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer / referrer bottom note */}
            <div className="border-t border-card py-8">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 text-center">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                        {info.tier} referrer · Invitation valid forever
                    </div>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 block">{label}</label>
        {children}
    </div>
);

const Input = (p) => (
    <input {...p} className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2.5 text-sm transition-colors" />
);

const Perk = ({ icon: Icon, label, value, testId }) => (
    <div className="bg-muted border border-border p-4 hover:border-primary/40 transition-colors" data-testid={testId}>
        <Icon className="w-5 h-5 text-primary mb-2" strokeWidth={1.2}/>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground mt-1">{value}</div>
    </div>
);
